/*
 * Bernard James — cart page, per-line note.
 *
 * Figma "Shopify Final" 1135:59095 draws an "ADD NOTE" simple-underline in
 * every cart line item (node 1138:61159) but never draws its open state, so
 * the entry UI is derived: the link toggles a textarea with APPLY / CANCEL.
 *
 * The value is stored as a visible line-item property (default key "Note", set
 * from the section so a merchant can rename it) via POST /cart/change.js, and
 * the response's rendered sections are swapped in — the same contract
 * assets/cart.js uses, so the cart totals and the header bag bubble stay in
 * sync without a page load.
 *
 * Depends on globals from assets/global.js (`routes`, `fetchConfig`), which is
 * loaded in <head> with defer, i.e. before this file.
 */
(function () {
  'use strict';

  var SECTIONS = ['main-cart-items', 'cart-icon-bubble', 'cart-live-region-text'];

  function sectionInnerHTML(html, selector) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var node = doc.querySelector(selector);
    return node ? node.innerHTML : null;
  }

  function replaceSections(sections) {
    if (!sections) return;

    var main = document.getElementById('main-cart-items');
    var mainId = main && main.dataset.id;
    var mainHTML = mainId ? sections[mainId] : null;

    if (mainHTML) {
      var contents = main.querySelector('.js-contents');
      var newContents = sectionInnerHTML(mainHTML, '.js-contents');
      if (contents && newContents !== null) contents.innerHTML = newContents;

      var footer = document.getElementById('main-cart-footer');
      var totals = footer && footer.querySelector('.js-contents-totals');
      var newTotals = sectionInnerHTML(mainHTML, '.js-contents-totals');
      if (totals && newTotals !== null) totals.innerHTML = newTotals;
    }

    ['cart-icon-bubble', 'cart-live-region-text'].forEach(function (id) {
      var target = document.getElementById(id);
      if (!target || !sections[id]) return;
      var inner = sectionInnerHTML(sections[id], '.shopify-section');
      if (inner !== null) target.innerHTML = inner;
    });
  }

  class CartLineNote extends HTMLElement {
    connectedCallback() {
      if (this.wired) return;
      this.wired = true;

      this.toggleButton = this.querySelector('[data-note-toggle]');
      this.editor = this.querySelector('.bj-cart-item__note-editor');
      this.input = this.querySelector('[data-note-input]');

      if (!this.toggleButton || !this.editor || !this.input) return;

      this.initialValue = this.input.value;

      /*
       * assets/cart.js binds a debounced `change` listener on <cart-items> and
       * its handler assumes every change came from a quantity input:
       *
       *   onChange(e) { this.updateQuantity(e.target.dataset.index, e.target.value, ...) }
       *
       * This textarea is a descendant of <cart-items>, so blurring it after an
       * edit bubbled a `change` up and made cart.js POST /cart/change.js with
       * `line: undefined, quantity: "<the note text>"`. Shopify answered 400
       * and cart.js painted "There was an error while updating your cart."
       * over a save that had actually succeeded.
       *
       * Containing the event here rather than patching stock cart.js: any
       * future field added inside <cart-items> needs the same guard.
       */
      this.addEventListener('change', function (event) {
        event.stopPropagation();
      });

      this.toggleButton.addEventListener('click', this.toggle.bind(this));
      this.querySelector('[data-note-save]').addEventListener('click', this.save.bind(this));
      this.querySelector('[data-note-cancel]').addEventListener('click', this.cancel.bind(this));
      this.input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') this.cancel();
      });
    }

    get isOpen() {
      return !this.editor.hasAttribute('hidden');
    }

    toggle() {
      if (this.isOpen) {
        this.cancel();
        return;
      }
      this.editor.removeAttribute('hidden');
      this.toggleButton.setAttribute('aria-expanded', 'true');
      this.input.focus();
    }

    cancel() {
      this.input.value = this.initialValue;
      this.editor.setAttribute('hidden', '');
      this.toggleButton.setAttribute('aria-expanded', 'false');
      this.toggleButton.focus();
    }

    save() {
      var key = this.dataset.key;
      var noteKey = this.dataset.noteKey || 'Note';
      var quantity = parseInt(this.dataset.quantity, 10) || 1;
      var value = this.input.value.trim();

      if (value === this.initialValue.trim()) {
        this.cancel();
        return;
      }

      var properties = {};
      properties[noteKey] = value;

      var body = JSON.stringify({
        id: key,
        quantity: quantity,
        properties: properties,
        sections: SECTIONS,
        sections_url: window.location.pathname,
      });

      this.classList.add('is-busy');

      fetch(routes.cart_change_url, Object.assign({}, fetchConfig(), { body: body }))
        .then(function (response) {
          return response.json();
        })
        .then(
          function (state) {
            if (state.status || state.errors) {
              this.classList.remove('is-busy');
              var errors = document.getElementById('cart-errors');
              if (errors) errors.textContent = state.description || state.message || state.errors;
              return;
            }
            replaceSections(state.sections);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, { source: 'bj-cart-line-note' });
            }
          }.bind(this)
        )
        .catch(
          function () {
            this.classList.remove('is-busy');
            var errors = document.getElementById('cart-errors');
            if (errors && window.cartStrings) errors.textContent = window.cartStrings.error;
          }.bind(this)
        );
    }
  }

  if (!customElements.get('cart-line-note')) {
    customElements.define('cart-line-note', CartLineNote);
  }

  /*
   * "BAG (N)" page title. It lives above everything cart.js re-renders, so
   * without this it kept the server-rendered count after a REMOVE.
   */
  function refreshTitle() {
    var el = document.querySelector('[data-bj-cart-title]');
    if (!el) return;
    fetch(routes.cart_url + '.js')
      .then(function (r) {
        return r.json();
      })
      .then(function (cart) {
        el.textContent = cart.item_count
          ? (el.dataset.labelCount || '').replace('__n__', cart.item_count)
          : el.dataset.labelEmpty || '';
      })
      .catch(function () {});
  }

  document.addEventListener('DOMContentLoaded', function () {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, refreshTitle);
    }
  });
})();
